import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Switch } from '../components/ui/switch';
import { FoliosAlegraTab } from './contabilidad/FoliosAlegraTab';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  Activity, Clock, AlertTriangle, Database, Cpu, Shield,
  CheckCircle2, AlertCircle, XCircle, RefreshCw, Mail, Users, Link2, Download, GitBranch, Hash,
} from 'lucide-react';

const SEM_COLORS = {
  green: 'bg-emerald-50 border-emerald-300 text-emerald-800',
  amber: 'bg-amber-50 border-amber-300 text-amber-800',
  red: 'bg-red-50 border-red-300 text-red-800',
};
const SEM_ICONS = {
  green: <CheckCircle2 className="w-4 h-4 text-emerald-600" />,
  amber: <AlertCircle className="w-4 h-4 text-amber-600" />,
  red: <XCircle className="w-4 h-4 text-red-600" />,
};

const TABS = [
  { id: 'resumen', label: 'Resumen', icon: Activity },
  { id: 'crons', label: 'Crons', icon: Clock },
  { id: 'integraciones', label: 'Integraciones', icon: Cpu },
  { id: 'errores', label: 'Errores 500', icon: AlertTriangle },
  { id: 'duplicados', label: 'Anti-duplicados', icon: Shield },
  { id: 'proveedores', label: 'Salud Proveedores', icon: Users },
  { id: 'integracion', label: 'Salud Integración', icon: Link2 },
  { id: 'proformas_cruzadas', label: 'Vinculación Proforma↔Alegra', icon: GitBranch },
  { id: 'folios_alegra', label: 'Folios DTE↔Alegra', icon: Hash },
  { id: 'razon_social', label: 'Razón Social Placeholder', icon: Users },
  { id: 'mongodb', label: 'MongoDB', icon: Database },
];

const fmtHrs = (h) => h == null ? '—' : h < 0.05 ? `${Math.round(h * 60)}min` : `${h.toFixed(1)}h`;
const fmtNum = (n) => (n ?? 0).toLocaleString('es-CL');
const truncate = (s, n) => (s || '').length > n ? (s || '').slice(0, n) + '…' : (s || '');

export default function CodeHealthPage() {
  const [tab, setTab] = useState('resumen');
  const [dashboard, setDashboard] = useState(null);
  const [crons, setCrons] = useState(null);
  const [integ, setInteg] = useState(null);
  const [errores, setErrores] = useState(null);
  const [dup, setDup] = useState(null);
  const [mongo, setMongo] = useState(null);
  const [prov, setProv] = useState(null);
  const [provVerifying, setProvVerifying] = useState(false);
  const [integH, setIntegH] = useState(null);
  const [proformasCruz, setProformasCruz] = useState(null);
  const [razonSocial, setRazonSocial] = useState(null);
  const [foliosPend, setFoliosPend] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const calls = [
        api.get('/admin/code-health/dashboard').then(r => setDashboard(r.data)).catch(() => {}),
        api.get('/admin/code-health/crons').then(r => setCrons(r.data)).catch(() => {}),
        api.get('/admin/code-health/integrations').then(r => setInteg(r.data)).catch(() => {}),
        api.get('/admin/code-health/errors?horas=24&limit=100').then(r => setErrores(r.data)).catch(() => {}),
        api.get('/admin/code-health/dup-stats?dias=7').then(r => setDup(r.data)).catch(() => {}),
        api.get('/admin/code-health/mongo-stats').then(r => setMongo(r.data)).catch(() => {}),
        api.get('/admin/code-health/proveedores-health').then(r => setProv(r.data)).catch(() => {}),
        api.get('/admin/code-health/integracion-health').then(r => setIntegH(r.data)).catch(() => {}),
        api.get('/admin/code-health/proformas-cruzadas').then(r => setProformasCruz(r.data)).catch(() => {}),
        api.get('/admin/code-health/razon-social-placeholder').then(r => setRazonSocial(r.data)).catch(() => {}),
        api.get('/contabilidad/auditoria-folios-alegra/pendientes/contador').then(r => setFoliosPend(r.data)).catch(() => {}),
      ];
      await Promise.all(calls);
    } catch (err) {
      toast.error('Error al cargar Code Health');
    } finally {
      setLoading(false);
    }
  }, []);

  const verifyProvAlegra = useCallback(async () => {
    setProvVerifying(true);
    try {
      const r = await api.get('/admin/code-health/proveedores-health?verify_alegra=true');
      setProv(r.data);
      toast.success(`Verificación Alegra completa · ${r.data?.resumen?.alegra_mismatch || 0} desfases`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al verificar contra Alegra');
    } finally {
      setProvVerifying(false);
    }
  }, []);

  const [provExporting, setProvExporting] = useState(false);
  const exportProvExcel = useCallback(async () => {
    setProvExporting(true);
    try {
      const res = await api.get('/admin/code-health/proveedores-health/export', { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `salud_proveedores_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success('Excel de Salud de Proveedores descargado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al descargar el Excel');
    } finally {
      setProvExporting(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(loadAll, 30000);
    return () => clearInterval(id);
  }, [autoRefresh, loadAll]);

  return (
    <div className="p-6 max-w-[1500px] mx-auto" data-testid="code-health-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Activity className="w-6 h-6 text-indigo-600" />
            Code Health Dashboard
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Monitoreo operativo: crons, integraciones, errores y MongoDB.
            Alertas por email a <code className="bg-gray-100 px-1 rounded text-xs">c.chirino@chirinosteel.com</code> cuando algún cron/integración falla.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={autoRefresh} onCheckedChange={setAutoRefresh} data-testid="toggle-autorefresh" />
            Auto-refresh 30s
          </label>
          <button
            onClick={loadAll}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded text-sm hover:bg-indigo-700 disabled:opacity-50"
            data-testid="btn-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b mb-4">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition ${
                tab === t.id ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500 hover:text-gray-800'
              }`}
              data-testid={`tab-${t.id}`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
              {t.id === 'folios_alegra' && foliosPend?.pendientes > 0 && (
                <span className="ml-1 inline-flex items-center justify-center rounded-full bg-red-600 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white" data-testid="folios-pend-badge" title={`${foliosPend.pendientes} folios cruzados pendientes`}>
                  {foliosPend.pendientes}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {tab === 'resumen' && <ResumenTab data={dashboard} integ={integ} crons={crons} />}
      {tab === 'crons' && <CronsTab data={crons} reload={loadAll} />}
      {tab === 'integraciones' && <IntegracionesTab data={integ} />}
      {tab === 'errores' && <ErroresTab data={errores} reload={loadAll} />}
      {tab === 'duplicados' && <DuplicadosTab data={dup} />}
      {tab === 'proveedores' && <ProveedoresTab data={prov} onVerify={verifyProvAlegra} verifying={provVerifying} onExport={exportProvExcel} exporting={provExporting} />}
      {tab === 'integracion' && <IntegracionTab data={integH} />}
      {tab === 'proformas_cruzadas' && <ProformasCruzadasTab data={proformasCruz} />}
      {tab === 'folios_alegra' && <FoliosAlegraTab />}
      {tab === 'razon_social' && <RazonSocialTab data={razonSocial} onRefresh={loadAll} />}
      {tab === 'mongodb' && <MongoTab data={mongo} />}
    </div>
  );
}

// ───── Resumen ─────
function ResumenTab({ data, integ, crons }) {
  if (!data) return <div className="text-gray-500" data-testid="loading-resumen">Cargando…</div>;
  const cards = [
    { label: 'Crons OK', value: data.crons.green, total: data.crons.total, color: 'emerald', testid: 'kpi-crons-green' },
    { label: 'Crons en alerta', value: data.crons.amber, color: 'amber', testid: 'kpi-crons-amber' },
    { label: 'Crons en falla', value: data.crons.red, color: 'red', testid: 'kpi-crons-red' },
    { label: 'Errores 500 (24h)', value: data.errors_500.ultimas_24h, color: data.errors_500.ultimas_24h > 5 ? 'red' : 'gray', testid: 'kpi-errors-24h' },
    { label: 'Errores 500 (1h)', value: data.errors_500.ultima_hora, color: data.errors_500.ultima_hora > 0 ? 'amber' : 'gray', testid: 'kpi-errors-1h' },
    { label: 'Anti-dup bloqueados (7d)', value: data.anti_duplicados_bloqueados_7d, color: 'blue', testid: 'kpi-dup-7d' },
    { label: 'Integraciones OK', value: data.integraciones.ok, total: data.integraciones.total, color: 'emerald', testid: 'kpi-integ-ok' },
  ];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {cards.map(c => (
          <div key={c.label} className={`p-4 rounded-lg border bg-${c.color}-50 border-${c.color}-200`} data-testid={c.testid}>
            <div className={`text-2xl font-bold text-${c.color}-700`}>
              {fmtNum(c.value)}{c.total != null && <span className="text-sm text-gray-500"> / {c.total}</span>}
            </div>
            <div className="text-xs text-gray-600 mt-1">{c.label}</div>
          </div>
        ))}
      </div>

      {/* Crons en falla destacados */}
      {crons && crons.items.filter(c => c.semaforo === 'red').length > 0 && (
        <div className="border border-red-300 rounded-lg p-4 bg-red-50">
          <div className="font-semibold text-red-800 mb-2 flex items-center gap-2">
            <XCircle className="w-5 h-5" /> Crons en falla — requieren atención
          </div>
          <ul className="space-y-1 text-sm">
            {crons.items.filter(c => c.semaforo === 'red').map(c => (
              <li key={c.cron_id} className="text-red-700">
                <strong>{c.label}</strong> — última corrida hace {fmtHrs(c.hours_since_last_run)} (esperada cada {c.frecuencia_esperada_horas}h)
                {c.error && <div className="text-xs text-red-600 ml-4 mt-0.5">⚠️ {truncate(c.error, 200)}</div>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Integraciones en falla */}
      {integ && integ.items.filter(i => i.semaforo !== 'green').length > 0 && (
        <div className="border border-amber-300 rounded-lg p-4 bg-amber-50">
          <div className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" /> Integraciones a revisar
          </div>
          <ul className="space-y-1 text-sm">
            {integ.items.filter(i => i.semaforo !== 'green').map(i => (
              <li key={i.name}>
                <span className="text-amber-700">{i.name}</span> — errores 24h: <strong>{i.errors_24h}</strong>
                {i.hours_since_heartbeat != null && <> · última señal hace {fmtHrs(i.hours_since_heartbeat)}</>}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="text-xs text-gray-400">Último check: {data.ultimo_check?.replace('T', ' ').slice(0, 19)} UTC</div>
    </div>
  );
}

// ───── Crons ─────
function CronsTab({ data, reload }) {
  const [triggering, setTriggering] = useState(null);
  const [confirmCron, setConfirmCron] = useState(null);

  const doTrigger = async () => {
    if (!confirmCron) return;
    const { cronId, label } = confirmCron;
    setConfirmCron(null);
    setTriggering(cronId);
    try {
      const res = await api.post(`/admin/code-health/crons/${cronId}/trigger`);
      toast.success(res.data.message || `Cron ${label} disparado`);
      setTimeout(reload, 2000);  // refresh tras 2s para ver el resultado
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al disparar cron');
    } finally {
      setTriggering(null);
    }
  };

  if (!data) return <div className="text-gray-500">Cargando…</div>;
  return (
    <>
    <div className="border rounded-lg overflow-hidden" data-testid="crons-table">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 text-xs uppercase text-gray-500">
          <tr>
            <th className="px-3 py-2 text-left">Estado</th>
            <th className="px-3 py-2 text-left">Cron</th>
            <th className="px-3 py-2 text-left">Categoría</th>
            <th className="px-3 py-2 text-left">Última corrida</th>
            <th className="px-3 py-2 text-left">Frec. esperada</th>
            <th className="px-3 py-2 text-left">Status</th>
            <th className="px-3 py-2 text-left">Stats</th>
            <th className="px-3 py-2 text-left">Auto-recovery</th>
            <th className="px-3 py-2 text-center">Acción</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {data.items.map(c => (
            <tr key={c.cron_id} className={c.semaforo === 'red' ? 'bg-red-50' : c.semaforo === 'amber' ? 'bg-amber-50' : ''} data-testid={`row-cron-${c.cron_id}`}>
              <td className="px-3 py-2">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs ${SEM_COLORS[c.semaforo]}`}>
                  {SEM_ICONS[c.semaforo]} {c.semaforo}
                </span>
              </td>
              <td className="px-3 py-2 font-medium">{c.label}</td>
              <td className="px-3 py-2"><span className="text-xs text-gray-500">{c.categoria}</span></td>
              <td className="px-3 py-2 text-xs">
                {c.last_run ? (<><div>{c.last_run.replace('T', ' ').slice(0, 19)}</div><div className="text-gray-500">hace {fmtHrs(c.hours_since_last_run)}</div></>) : <span className="text-gray-400">—</span>}
              </td>
              <td className="px-3 py-2 text-xs text-gray-500">cada {c.frecuencia_esperada_horas}h</td>
              <td className="px-3 py-2 text-xs">
                {c.running ? <span className="text-blue-600">⏳ running</span> : c.status === 'error' ? <span className="text-red-600">❌ error</span> : c.status === 'success' ? <span className="text-emerald-600">✅ OK</span> : <span className="text-gray-500">{c.status}</span>}
              </td>
              <td className="px-3 py-2 text-xs text-gray-600">{c.stats_resumen || '—'}</td>
              <td className="px-3 py-2 text-xs">
                {c.last_recovery ? (
                  <div title={c.last_recovery.details}>
                    <div className={`font-medium ${c.last_recovery.result === 'triggered' ? 'text-blue-700' : c.last_recovery.result === 'triggered_manual' ? 'text-purple-700' : 'text-amber-700'}`}>
                      {c.last_recovery.result === 'triggered' ? '🔄 auto' : c.last_recovery.result === 'triggered_manual' ? '👤 manual' : c.last_recovery.result}
                    </div>
                    <div className="text-gray-500 text-[10px]">{c.last_recovery.triggered_at?.replace('T', ' ').slice(0, 16)}</div>
                  </div>
                ) : <span className="text-gray-400">—</span>}
              </td>
              <td className="px-3 py-2 text-center">
                <button
                  onClick={() => setConfirmCron({ cronId: c.cron_id, label: c.label })}
                  disabled={triggering === c.cron_id || c.running}
                  className="text-xs px-2 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                  title="Disparar este cron ahora (sin esperar al próximo schedule)"
                  data-testid={`btn-trigger-${c.cron_id}`}
                >
                  {triggering === c.cron_id ? '⏳' : '▶️ Trigger'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

      <AlertDialog open={confirmCron !== null} onOpenChange={(o) => { if (!o) setConfirmCron(null); }}>
        <AlertDialogContent data-testid="trigger-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Disparar cron manualmente</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Deseas disparar ahora el cron <strong>"{confirmCron?.label}"</strong> sin esperar
              al próximo schedule? Esta acción se ejecuta de inmediato.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="trigger-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doTrigger} data-testid="trigger-confirm">
              Disparar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ───── Integraciones ─────
function IntegracionesTab({ data }) {
  if (!data) return <div className="text-gray-500">Cargando…</div>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="integraciones-grid">
      {data.items.map(i => (
        <div key={i.name} className={`p-4 rounded-lg border ${SEM_COLORS[i.semaforo]}`} data-testid={`integ-${i.name.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="font-semibold flex items-center gap-2">{SEM_ICONS[i.semaforo]} {i.name}</div>
            <span className="text-xs px-2 py-0.5 bg-white/50 rounded">{i.categoria}</span>
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <div className="text-xs opacity-70">Última señal</div>
              <div className="font-mono">{i.hours_since_heartbeat != null ? `hace ${fmtHrs(i.hours_since_heartbeat)}` : '—'}</div>
            </div>
            <div>
              <div className="text-xs opacity-70">Errores 24h</div>
              <div className="font-mono font-bold">{fmtNum(i.errors_24h)}</div>
            </div>
          </div>
          {i.last_heartbeat && <div className="text-xs opacity-60 mt-2">{i.last_heartbeat.replace('T', ' ').slice(0, 19)} UTC</div>}
        </div>
      ))}
    </div>
  );
}

// ───── Errores 500 ─────
function ErroresTab({ data }) {
  if (!data) return <div className="text-gray-500">Cargando…</div>;
  return (
    <div className="space-y-4">
      <div className="border rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-600">Top endpoints con errores ({data.ventana_horas}h)</div>
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500"><tr>
            <th className="px-3 py-2 text-left">Endpoint</th>
            <th className="px-3 py-2 text-left">Status</th>
            <th className="px-3 py-2 text-right">Count</th>
            <th className="px-3 py-2 text-left">Última</th>
          </tr></thead>
          <tbody className="divide-y">
            {(data.agrupados_por_endpoint || []).map((g, idx) => (
              <tr key={idx} data-testid={`error-group-${idx}`}>
                <td className="px-3 py-2 font-mono text-xs">{g.endpoint}</td>
                <td className="px-3 py-2"><span className={`px-1.5 py-0.5 rounded text-xs ${g.status_code === 500 ? 'bg-red-100 text-red-700' : g.status_code === 409 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-700'}`}>{g.status_code}</span></td>
                <td className="px-3 py-2 text-right font-mono font-bold">{g.count}</td>
                <td className="px-3 py-2 text-xs text-gray-500">{(g.last_seen || '').replace('T', ' ').slice(0, 19)}</td>
              </tr>
            ))}
            {(data.agrupados_por_endpoint || []).length === 0 && (
              <tr><td colSpan={4} className="px-3 py-6 text-center text-emerald-600 text-sm">✅ Sin errores en las últimas {data.ventana_horas}h</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-600">Eventos individuales recientes ({data.total})</div>
        <div className="max-h-[500px] overflow-y-auto">
          {(data.items || []).map((e, idx) => (
            <div key={idx} className="px-3 py-2 border-b text-xs hover:bg-gray-50" data-testid={`error-item-${idx}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className={`px-1.5 py-0.5 rounded ${e.status_code >= 500 ? 'bg-red-100 text-red-700' : e.status_code === 409 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100'}`}>{e.status_code}</span>
                <span className="font-mono text-blue-700">{e.method} {e.endpoint}</span>
                <span className="text-gray-500 ml-auto">{(e.timestamp || '').replace('T', ' ').slice(0, 19)}</span>
              </div>
              <div className="text-gray-700 ml-2">{truncate(e.message, 250)}</div>
              {e.user_email && <div className="text-gray-400 ml-2 mt-0.5">user: {e.user_email}</div>}
            </div>
          ))}
          {(data.items || []).length === 0 && <div className="px-3 py-6 text-center text-emerald-600 text-sm">✅ Sin eventos</div>}
        </div>
      </div>
    </div>
  );
}

// ───── Anti-duplicados ─────
function DuplicadosTab({ data }) {
  if (!data) return <div className="text-gray-500">Cargando…</div>;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <div className="p-4 border rounded bg-blue-50 border-blue-200" data-testid="kpi-409">
          <div className="text-2xl font-bold text-blue-700">{fmtNum(data.total_409_bloqueados)}</div>
          <div className="text-xs text-gray-600 mt-1">HTTP 409 bloqueados ({data.ventana_dias}d)</div>
        </div>
        <div className="p-4 border rounded bg-amber-50 border-amber-200" data-testid="kpi-anuladas">
          <div className="text-2xl font-bold text-amber-700">{fmtNum(data.ocs_anuladas_duplicadas)}</div>
          <div className="text-xs text-gray-600 mt-1">OCs Express anuladas como dup ({data.ventana_dias}d)</div>
        </div>
        <div className="p-4 border rounded bg-emerald-50 border-emerald-200" data-testid="kpi-reconciliados">
          <div className="text-2xl font-bold text-emerald-700">{fmtNum(data.dtes_reconciliados)}</div>
          <div className="text-xs text-gray-600 mt-1">DTEs reconciliados RCV→XML ({data.ventana_dias}d)</div>
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-600">Endpoints con más bloqueos 409</div>
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500"><tr>
            <th className="px-3 py-2 text-left">Endpoint</th>
            <th className="px-3 py-2 text-right">Bloqueos</th>
            <th className="px-3 py-2 text-left">Última</th>
            <th className="px-3 py-2 text-left">Mensaje ejemplo</th>
          </tr></thead>
          <tbody className="divide-y">
            {(data.bloqueados_por_endpoint || []).map((g, idx) => (
              <tr key={idx} data-testid={`dup-row-${idx}`}>
                <td className="px-3 py-2 font-mono text-xs">{g.endpoint}</td>
                <td className="px-3 py-2 text-right font-mono font-bold">{g.count}</td>
                <td className="px-3 py-2 text-xs text-gray-500">{(g.last_seen || '').replace('T', ' ').slice(0, 19)}</td>
                <td className="px-3 py-2 text-xs">{truncate(g.sample_msg, 120)}</td>
              </tr>
            ))}
            {(data.bloqueados_por_endpoint || []).length === 0 && (
              <tr><td colSpan={4} className="px-3 py-6 text-center text-gray-500 text-sm">Sin 409 en la ventana</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ───── Salud Proveedores ─────
function ProveedoresTab({ data, onVerify, verifying, onExport, exporting }) {
  if (!data) return <div className="text-gray-500" data-testid="loading-proveedores">Cargando…</div>;
  const r = data.resumen || {};
  const cards = [
    { label: 'rut_proveedor huérfano', value: r.huerfanos, color: r.huerfanos > 0 ? 'red' : 'emerald', testid: 'kpi-prov-huerfanos' },
    { label: 'Proveedores partidos', value: r.partidos, color: r.partidos > 0 ? 'red' : 'emerald', testid: 'kpi-prov-partidos' },
    { label: 'Pendiente a inactivo', value: r.pendientes_a_inactivo, color: r.pendientes_a_inactivo > 0 ? 'amber' : 'emerald', testid: 'kpi-prov-inactivo' },
    { label: 'Contactos duplicados', value: r.duplicados, color: r.duplicados > 0 ? 'amber' : 'emerald', testid: 'kpi-prov-duplicados' },
    { label: 'Desfase vs Alegra', value: r.alegra_mismatch, color: r.alegra_mismatch > 0 ? 'red' : 'gray', testid: 'kpi-prov-mismatch' },
  ];
  return (
    <div className="space-y-4" data-testid="proveedores-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Consistencia entre el espejo de contactos Alegra y las facturas de compra. Detecta el tipo de problema del caso Dimerc.
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={onExport}
            disabled={exporting}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50 disabled:opacity-50"
            data-testid="btn-export-proveedores-excel"
          >
            <Download className={`w-4 h-4 ${exporting ? 'animate-pulse' : ''}`} />
            {exporting ? 'Generando…' : 'Descargar Excel'}
          </button>
          <button
            onClick={onVerify}
            disabled={verifying}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white rounded text-sm hover:bg-emerald-700 disabled:opacity-50"
            data-testid="btn-verify-alegra"
          >
            <RefreshCw className={`w-4 h-4 ${verifying ? 'animate-spin' : ''}`} />
            {verifying ? 'Verificando…' : 'Verificar contra Alegra (en vivo)'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {cards.map(c => (
          <div key={c.label} className={`p-4 rounded-lg border bg-${c.color}-50 border-${c.color}-200`} data-testid={c.testid}>
            <div className={`text-2xl font-bold text-${c.color}-700`}>{fmtNum(c.value)}</div>
            <div className="text-xs text-gray-600 mt-1">{c.label}</div>
          </div>
        ))}
      </div>

      {/* Huérfanos */}
      <ProvSection title="rut_proveedor huérfano (facturas que no resuelven a un contacto)" rows={data.huerfanos} testid="prov-huerfanos" empty="Sin huérfanos ✅">
        {(data.huerfanos || []).map((x, i) => (
          <tr key={i} className="border-b" data-testid={`huerfano-${i}`}>
            <td className="px-3 py-2 font-mono text-xs">{x.clave || '—'}</td>
            <td className="px-3 py-2">{x.nombre || '—'}</td>
            <td className="px-3 py-2 text-right">{fmtNum(x.facturas)}</td>
            <td className="px-3 py-2 text-right">{fmtNum(x.pendientes)}</td>
            <td className="px-3 py-2 text-right font-mono">${fmtNum(x.monto_pendiente)}</td>
            <td className="px-3 py-2 text-xs text-gray-500">{x.motivo}</td>
          </tr>
        ))}
      </ProvSection>

      {/* Partidos */}
      <ProvSection title="Proveedores partidos (mismo RUT con varias claves)" rows={data.partidos} testid="prov-partidos" empty="Sin proveedores partidos ✅"
        head={<tr><th className="px-3 py-2 text-left">RUT real</th><th className="px-3 py-2 text-left">Nombre</th><th className="px-3 py-2 text-left">Claves usadas</th><th className="px-3 py-2 text-right">Facturas</th><th className="px-3 py-2 text-right">Pend.</th><th className="px-3 py-2 text-right">$ Pendiente</th></tr>}>
        {(data.partidos || []).map((x, i) => (
          <tr key={i} className="border-b" data-testid={`partido-${i}`}>
            <td className="px-3 py-2 font-mono text-xs">{x.rut_real}</td>
            <td className="px-3 py-2">{x.nombre || '—'}</td>
            <td className="px-3 py-2 text-xs">{(x.claves || []).map(k => `${k.clave} (${k.facturas})`).join('  ·  ')}</td>
            <td className="px-3 py-2 text-right">{fmtNum(x.facturas)}</td>
            <td className="px-3 py-2 text-right">{fmtNum(x.pendientes)}</td>
            <td className="px-3 py-2 text-right font-mono">${fmtNum(x.monto_pendiente)}</td>
          </tr>
        ))}
      </ProvSection>

      {/* Pendiente a inactivo */}
      <ProvSection title="Facturas pendientes a contacto inactivo/desactivado" rows={data.pendientes_a_inactivo} testid="prov-inactivo" empty="Sin facturas pendientes a inactivos ✅">
        {(data.pendientes_a_inactivo || []).map((x, i) => (
          <tr key={i} className="border-b" data-testid={`inactivo-${i}`}>
            <td className="px-3 py-2 font-mono text-xs">{x.clave}</td>
            <td className="px-3 py-2">{x.nombre || '—'}</td>
            <td className="px-3 py-2 text-right">{fmtNum(x.pendientes)}</td>
            <td className="px-3 py-2 text-right font-mono">${fmtNum(x.monto_pendiente)}</td>
            <td className="px-3 py-2 text-xs text-gray-500">{x.motivo}</td>
          </tr>
        ))}
      </ProvSection>

      {/* Duplicados */}
      <div className="border rounded-lg overflow-hidden" data-testid="prov-duplicados">
        <div className="bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-600">Contactos duplicados en el espejo (misma identificación)</div>
        <div className="divide-y">
          {(data.duplicados || []).length === 0 && <div className="px-3 py-6 text-center text-emerald-600 text-sm">Sin duplicados ✅</div>}
          {(data.duplicados || []).map((d, i) => (
            <div key={i} className="px-3 py-2 text-sm" data-testid={`duplicado-${i}`}>
              <span className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">{d.identificacion}</span>
              <span className="ml-2 text-xs text-gray-600">
                {d.contactos.map(c => `#${c.alegra_id} ${c.nombre}${c.inactivo ? ' (inactivo)' : ''}${c.es_proveedor ? ' [prov]' : ''}`).join('   //   ')}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Desfase vs Alegra */}
      {data.resumen?.verificado_en_alegra && (
        <div className="border rounded-lg overflow-hidden" data-testid="prov-mismatch">
          <div className="bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-600">Desfase con Alegra en vivo (contactos sospechosos)</div>
          <div className="divide-y">
            {(data.alegra_mismatch || []).length === 0 && <div className="px-3 py-6 text-center text-emerald-600 text-sm">Espejo alineado con Alegra ✅</div>}
            {(data.alegra_mismatch || []).map((m, i) => (
              <div key={i} className="px-3 py-2 text-sm" data-testid={`mismatch-${i}`}>
                <span className={`px-1.5 py-0.5 rounded text-xs ${m.issue === 'eliminado_en_alegra' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{m.issue}</span>
                <span className="ml-2 font-mono text-xs">#{m.alegra_id}</span>
                <span className="ml-2 text-gray-700">{m.espejo_nombre || m.alegra_nombre}</span>
                <div className="text-xs text-gray-500 ml-1 mt-0.5">{m.detalle}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="text-xs text-gray-400">Último check: {(data.checked_at || '').replace('T', ' ').slice(0, 19)} UTC</div>
    </div>
  );
}

function ProvSection({ title, rows, testid, empty, head, children }) {
  return (
    <div className="border rounded-lg overflow-hidden" data-testid={testid}>
      <div className="bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-600">{title} ({(rows || []).length})</div>
      {(rows || []).length === 0 ? (
        <div className="px-3 py-6 text-center text-emerald-600 text-sm">{empty}</div>
      ) : (
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            {head || <tr><th className="px-3 py-2 text-left">Clave</th><th className="px-3 py-2 text-left">Nombre</th><th className="px-3 py-2 text-right">Facturas</th><th className="px-3 py-2 text-right">Pend.</th><th className="px-3 py-2 text-right">$ Pendiente</th><th className="px-3 py-2 text-left">Motivo</th></tr>}
          </thead>
          <tbody>{children}</tbody>
        </table>
      )}
    </div>
  );
}

// ───── Salud Integración (CHS-Comercial) ─────
function IntegracionTab({ data, reload }) {
  const [correcting, setCorrecting] = useState(null);
  const [confirmRow, setConfirmRow] = useState(null);
  if (!data) return <div className="text-gray-500" data-testid="loading-integracion">Cargando…</div>;

  const doCorregir = async (numeroOt) => {
    setConfirmRow(null);
    setCorrecting(numeroOt);
    try {
      const r = await api.post('/admin/code-health/integracion-health/corregir-numero', { numero_ot: numeroOt });
      toast.success(`OT ${numeroOt} corregida: "${truncate(r.data?.descripcion_nueva, 50)}"`);
      if (reload) await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al corregir el número');
    } finally {
      setCorrecting(null);
    }
  };

  const r = data.resumen || {};
  const cards = [
    { label: 'Desajuste de número', value: r.desajuste_numero, color: r.desajuste_numero > 0 ? 'red' : 'emerald', testid: 'kpi-integ-desajuste' },
    { label: 'OTs huérfanas', value: r.huerfanas, color: r.huerfanas > 0 ? 'red' : 'emerald', testid: 'kpi-integ-huerfanas' },
    { label: 'Sin responsable', value: r.sin_responsable, color: r.sin_responsable > 0 ? 'amber' : 'emerald', testid: 'kpi-integ-sin-responsable' },
    { label: 'Sin monto', value: r.sin_monto, color: r.sin_monto > 0 ? 'amber' : 'emerald', testid: 'kpi-integ-sin-monto' },
    { label: 'Proyectos sin cliente', value: r.proyectos_sin_cliente, color: r.proyectos_sin_cliente > 0 ? 'amber' : 'emerald', testid: 'kpi-integ-proy-sin-cliente' },
  ];
  return (
    <div className="space-y-4" data-testid="integracion-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Calidad de las OTs y Proyectos que la app externa <strong>CHS-Comercial</strong> crea en el ERP
          (<code className="bg-gray-100 px-1 rounded text-xs">source=chs-comercial</code>). Solo lectura — no bloquea a la app externa.
        </p>
        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded border text-xs ${SEM_COLORS[data.semaforo]}`} data-testid="integracion-semaforo">
          {SEM_ICONS[data.semaforo]} {data.total_issues} problema(s)
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {cards.map(c => (
          <div key={c.label} className={`p-4 rounded-lg border bg-${c.color}-50 border-${c.color}-200`} data-testid={c.testid}>
            <div className={`text-2xl font-bold text-${c.color}-700`}>{fmtNum(c.value)}</div>
            <div className="text-xs text-gray-600 mt-1">{c.label}</div>
          </div>
        ))}
      </div>

      {/* Desajuste de número */}
      <ProvSection title="Desajuste de número (la descripción dice OT N pero el número real es otro)" rows={data.desajuste_numero} testid="integ-desajuste" empty="Sin desajustes de número ✅"
        head={<tr><th className="px-3 py-2 text-left">N° real</th><th className="px-3 py-2 text-left">N° en texto</th><th className="px-3 py-2 text-left">Proyecto</th><th className="px-3 py-2 text-left">Descripción</th><th className="px-3 py-2 text-left">Motivo</th><th className="px-3 py-2 text-center">Acción</th></tr>}>
        {(data.desajuste_numero || []).map((x, i) => (
          <tr key={i} className="border-b" data-testid={`integ-desajuste-${i}`}>
            <td className="px-3 py-2 font-mono text-xs font-bold">{x.numero_ot ?? '—'}</td>
            <td className="px-3 py-2 font-mono text-xs text-red-600">{x.numero_en_texto ?? '—'}</td>
            <td className="px-3 py-2 text-xs">{x.proyecto_codigo || '—'}</td>
            <td className="px-3 py-2 text-xs text-gray-600">{truncate(x.descripcion, 60)}</td>
            <td className="px-3 py-2 text-xs text-gray-500">{x.motivo}</td>
            <td className="px-3 py-2 text-center">
              <button
                onClick={() => setConfirmRow(x)}
                disabled={correcting === x.numero_ot}
                className="text-xs px-2 py-1 bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50"
                title={`Sincronizar la descripción con el número real ${x.numero_ot} (guarda respaldo)`}
                data-testid={`btn-corregir-${x.numero_ot}`}
              >
                {correcting === x.numero_ot ? '⏳' : '🔧 Corregir número'}
              </button>
            </td>
          </tr>
        ))}
      </ProvSection>

      {/* Huérfanas */}
      <ProvSection title="OTs huérfanas (proyecto_codigo no existe en proyectos)" rows={data.huerfanas} testid="integ-huerfanas" empty="Sin OTs huérfanas ✅"
        head={<tr><th className="px-3 py-2 text-left">N° OT</th><th className="px-3 py-2 text-left">proyecto_codigo</th><th className="px-3 py-2 text-left">Descripción</th><th className="px-3 py-2 text-left">Motivo</th></tr>}>
        {(data.huerfanas || []).map((x, i) => (
          <tr key={i} className="border-b" data-testid={`integ-huerfana-${i}`}>
            <td className="px-3 py-2 font-mono text-xs font-bold">{x.numero_ot ?? '—'}</td>
            <td className="px-3 py-2 font-mono text-xs">{x.proyecto_codigo || '—'}</td>
            <td className="px-3 py-2 text-xs text-gray-600">{truncate(x.descripcion, 60)}</td>
            <td className="px-3 py-2 text-xs text-gray-500">{x.motivo}</td>
          </tr>
        ))}
      </ProvSection>

      {/* Sin responsable */}
      <ProvSection title="OTs sin responsable" rows={data.sin_responsable} testid="integ-sin-responsable" empty="Todas las OTs tienen responsable ✅"
        head={<tr><th className="px-3 py-2 text-left">N° OT</th><th className="px-3 py-2 text-left">Proyecto</th><th className="px-3 py-2 text-left">Descripción</th></tr>}>
        {(data.sin_responsable || []).map((x, i) => (
          <tr key={i} className="border-b" data-testid={`integ-sin-responsable-${i}`}>
            <td className="px-3 py-2 font-mono text-xs font-bold">{x.numero_ot ?? '—'}</td>
            <td className="px-3 py-2 text-xs">{x.proyecto_codigo || '—'}</td>
            <td className="px-3 py-2 text-xs text-gray-600">{truncate(x.descripcion, 70)}</td>
          </tr>
        ))}
      </ProvSection>

      {/* Sin monto */}
      <ProvSection title="OTs sin monto (monto ≤ 0)" rows={data.sin_monto} testid="integ-sin-monto" empty="Todas las OTs tienen monto ✅"
        head={<tr><th className="px-3 py-2 text-left">N° OT</th><th className="px-3 py-2 text-left">Proyecto</th><th className="px-3 py-2 text-left">Descripción</th></tr>}>
        {(data.sin_monto || []).map((x, i) => (
          <tr key={i} className="border-b" data-testid={`integ-sin-monto-${i}`}>
            <td className="px-3 py-2 font-mono text-xs font-bold">{x.numero_ot ?? '—'}</td>
            <td className="px-3 py-2 text-xs">{x.proyecto_codigo || '—'}</td>
            <td className="px-3 py-2 text-xs text-gray-600">{truncate(x.descripcion, 70)}</td>
          </tr>
        ))}
      </ProvSection>

      {/* Proyectos sin cliente */}
      <ProvSection title="Proyectos (CHS) sin cliente" rows={data.proyectos_sin_cliente} testid="integ-proy-sin-cliente" empty="Todos los proyectos tienen cliente ✅"
        head={<tr><th className="px-3 py-2 text-left">Código</th><th className="px-3 py-2 text-left">Descripción</th><th className="px-3 py-2 text-left">Motivo</th></tr>}>
        {(data.proyectos_sin_cliente || []).map((x, i) => (
          <tr key={i} className="border-b" data-testid={`integ-proy-${i}`}>
            <td className="px-3 py-2 font-mono text-xs font-bold">{x.codigo || '—'}</td>
            <td className="px-3 py-2 text-xs text-gray-600">{truncate(x.descripcion, 70)}</td>
            <td className="px-3 py-2 text-xs text-gray-500">{x.motivo}</td>
          </tr>
        ))}
      </ProvSection>

      <div className="text-xs text-gray-400">Último check: {(data.checked_at || '').replace('T', ' ').slice(0, 19)} UTC</div>

      <AlertDialog open={confirmRow !== null} onOpenChange={(o) => { if (!o) setConfirmRow(null); }}>
        <AlertDialogContent data-testid="corregir-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Corregir número de la OT {confirmRow?.numero_ot}</AlertDialogTitle>
            <AlertDialogDescription>
              Se cambiará la descripción para que el número coincida con el real
              (<strong>{confirmRow?.numero_ot}</strong>). Se guarda respaldo de la
              descripción original.
              <br /><br />
              Actual: <span className="font-mono text-gray-700">"{confirmRow?.descripcion}"</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="corregir-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmRow && doCorregir(confirmRow.numero_ot)}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="corregir-confirm"
            >
              Corregir número
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ───── MongoDB ─────
function MongoTab({ data }) {
  if (!data) return <div className="text-gray-500">Cargando…</div>;
  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-600">Total colecciones: <strong>{data.total_collections}</strong></div>
      <div className="border rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-600">Top 25 colecciones por document count</div>
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500"><tr>
            <th className="px-3 py-2 text-left">Colección</th>
            <th className="px-3 py-2 text-right">Documentos</th>
          </tr></thead>
          <tbody className="divide-y">
            {(data.top_25_by_count || []).map((c, idx) => (
              <tr key={idx} data-testid={`coll-row-${idx}`}>
                <td className="px-3 py-2 font-mono text-xs">{c.collection}</td>
                <td className="px-3 py-2 text-right font-mono">{fmtNum(c.count)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-600">Índices (top 10 colecciones)</div>
        <div className="divide-y">
          {(data.index_summary || []).map((s, idx) => (
            <div key={idx} className="px-3 py-2" data-testid={`idx-row-${idx}`}>
              <div className="font-mono text-xs font-semibold">{s.collection} <span className="text-gray-500">({fmtNum(s.count)} docs)</span></div>
              <div className="flex flex-wrap gap-1 mt-1">
                {s.indexes.map(i => <span key={i} className="text-[10px] px-1.5 py-0.5 bg-gray-100 rounded font-mono">{i}</span>)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}



// ───── Vinculación Proforma ↔ Alegra (Iter 313.b) ─────
function ProformasCruzadasTab({ data }) {
  if (!data) return <div className="text-sm text-gray-500 p-8 text-center">Cargando…</div>;
  const cruzadas = data.cruzadas || [];
  const huerfanas = data.huerfanas || [];
  const totalIssues = (data.cruzadas_count || 0) + (data.huerfanas_count || 0);

  const sev = data.severidad || 'ok';
  const sevCfg = {
    ok:       { color: 'bg-emerald-100 text-emerald-800 border-emerald-300', label: 'Todo en orden', icon: <CheckCircle2 className="w-4 h-4" /> },
    warning:  { color: 'bg-amber-100 text-amber-800 border-amber-300',       label: 'Requiere atención', icon: <AlertCircle className="w-4 h-4" /> },
    critical: { color: 'bg-red-100 text-red-800 border-red-300',             label: 'Cruces detectados', icon: <XCircle className="w-4 h-4" /> },
  }[sev];

  return (
    <div className="space-y-4" data-testid="tab-proformas-cruzadas">
      <div className={`rounded-lg border p-4 flex items-center gap-3 ${sevCfg.color}`}>
        {sevCfg.icon}
        <div className="flex-1">
          <div className="font-semibold">{sevCfg.label}</div>
          <div className="text-xs opacity-80">
            {totalIssues === 0 ? (
              'Todas las proformas apuntan a una factura de Alegra con el folio correcto.'
            ) : (
              <>
                {data.cruzadas_count} cruce(s) — {data.huerfanas_count} huérfana(s) (alegra_invoice_id no está en espejo local).
              </>
            )}
          </div>
        </div>
        <div className="text-[11px] opacity-70">Chequeado: {(data.checked_at || '').slice(0, 19).replace('T', ' ')}</div>
      </div>

      {cruzadas.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <div className="bg-red-50 border-b border-red-200 px-4 py-2 font-semibold text-red-800 text-sm">
            🔴 Proformas cuyo alegra_invoice_id apunta a un folio distinto ({cruzadas.length})
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-3 py-2 text-left">Prof.</th>
                  <th className="px-3 py-2 text-left">Cliente ERP</th>
                  <th className="px-3 py-2 text-center">Folio ERP</th>
                  <th className="px-3 py-2 text-center">Alegra ID</th>
                  <th className="px-3 py-2 text-center">Folio Alegra</th>
                  <th className="px-3 py-2 text-left">Cliente Alegra</th>
                  <th className="px-3 py-2 text-right">Total Alegra</th>
                  <th className="px-3 py-2 text-center">Estado</th>
                </tr>
              </thead>
              <tbody>
                {cruzadas.map(c => (
                  <tr key={c.numero_proforma} className="border-t hover:bg-red-50/50" data-testid={`row-cruzada-${c.numero_proforma}`}>
                    <td className="px-3 py-2 font-mono font-semibold">#{c.numero_proforma}</td>
                    <td className="px-3 py-2">{c.cliente_erp}</td>
                    <td className="px-3 py-2 text-center font-mono text-blue-700 font-semibold">{c.folio_dte_erp}</td>
                    <td className="px-3 py-2 text-center font-mono text-gray-500">{c.alegra_invoice_id}</td>
                    <td className="px-3 py-2 text-center font-mono text-red-700 font-semibold">{c.folio_alegra}</td>
                    <td className="px-3 py-2 text-gray-700">{c.cliente_alegra || '-'}</td>
                    <td className="px-3 py-2 text-right font-mono">${fmtNum(c.total_alegra)}</td>
                    <td className="px-3 py-2 text-center">
                      <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 rounded uppercase">{c.estado_alegra || '-'}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="bg-red-50/50 px-4 py-2 text-[11px] text-red-700 border-t border-red-200">
            Para corregir: POST /api/facturacion/proformas/&#123;id&#125;/relinkear-dte o directo en la BD si es un cruce histórico.
          </div>
        </div>
      )}

      {huerfanas.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 font-semibold text-amber-800 text-sm">
            🟠 Proformas huérfanas: alegra_invoice_id no existe en el espejo local ({huerfanas.length})
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-3 py-2 text-left">Prof.</th>
                  <th className="px-3 py-2 text-left">Cliente</th>
                  <th className="px-3 py-2 text-center">Folio ERP</th>
                  <th className="px-3 py-2 text-center">Alegra ID</th>
                  <th className="px-3 py-2 text-center">Estado Prof.</th>
                  <th className="px-3 py-2 text-center">Origen link</th>
                </tr>
              </thead>
              <tbody>
                {huerfanas.map(h => (
                  <tr key={h.numero_proforma} className="border-t hover:bg-amber-50/50" data-testid={`row-huerfana-${h.numero_proforma}`}>
                    <td className="px-3 py-2 font-mono font-semibold">#{h.numero_proforma}</td>
                    <td className="px-3 py-2">{h.cliente_nombre}</td>
                    <td className="px-3 py-2 text-center font-mono">{h.folio_dte_erp}</td>
                    <td className="px-3 py-2 text-center font-mono text-gray-500">{h.alegra_invoice_id}</td>
                    <td className="px-3 py-2 text-center">{h.estado_proforma || '-'}</td>
                    <td className="px-3 py-2 text-center text-[10px] text-gray-600">{h.linked_origen || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="bg-amber-50/50 px-4 py-2 text-[11px] text-amber-800 border-t border-amber-200">
            Puede ser sync incremental desactualizado — re-corré POST /api/alegra-resync/invoices-pendientes.
          </div>
        </div>
      )}

      {cruzadas.length === 0 && huerfanas.length === 0 && (
        <div className="text-center py-12 text-gray-500 border rounded-lg">
          <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500 mb-2" />
          <p className="text-sm">Todos los punteros Proforma → Alegra están consistentes.</p>
        </div>
      )}
    </div>
  );
}

// ───── Razón Social Placeholder (Iter 313.c/d) ─────
function RazonSocialTab({ data, onRefresh }) {
  const [deleting, setDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [lastDeleteResult, setLastDeleteResult] = useState(null);
  if (!data) return <div className="text-sm text-gray-500 p-8 text-center">Cargando…</div>;
  const proveedores = data.proveedores || [];
  const ordenes = data.ordenes_compra || [];
  const alegra = data.alegra_contacts || [];
  const total = data.total || 0;
  const totalValidos = data.total_con_rut_valido || 0;
  const totalFantasmas = proveedores.filter(p => !p.rut_dv_valido).length;

  const handleBorrarFantasmas = async () => {
    setDeleting(true);
    try {
      const r = await api.post('/admin/code-health/razon-social-placeholder/borrar-fantasmas');
      setLastDeleteResult(r.data);
      toast.success(`Borrados ${r.data.borrados} proveedores fantasma`);
      setConfirmOpen(false);
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(`Error borrando fantasmas: ${e.response?.data?.detail || e.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const sev = data.severidad || 'ok';
  const sevCfg = {
    ok:       { color: 'bg-emerald-100 text-emerald-800 border-emerald-300', label: 'Sin placeholders pendientes', icon: <CheckCircle2 className="w-4 h-4" /> },
    warning:  { color: 'bg-amber-100 text-amber-800 border-amber-300',       label: `${total - totalValidos} placeholder(s) — solo RUTs falsos`, icon: <AlertCircle className="w-4 h-4" /> },
    critical: { color: 'bg-red-100 text-red-800 border-red-300',             label: `${totalValidos} placeholder(s) con RUT válido pendiente(s)`, icon: <XCircle className="w-4 h-4" /> },
  }[sev];

  const renderTable = (title, items, columns, testId) => (
    <div className="border rounded-lg overflow-hidden">
      <div className="bg-gray-50 border-b px-4 py-2 font-semibold text-gray-700 text-sm">
        {title} ({items.length})
      </div>
      {items.length === 0 ? (
        <div className="p-4 text-center text-gray-400 text-xs">Sin placeholders</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs" data-testid={testId}>
            <thead className="bg-gray-50 border-b">
              <tr>
                {columns.map(c => <th key={c.key} className="px-3 py-2 text-left">{c.label}</th>)}
                <th className="px-3 py-2 text-center">RUT válido</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => (
                <tr key={idx} className={`border-t hover:bg-gray-50 ${it.rut_dv_valido ? 'bg-red-50/30' : ''}`}>
                  {columns.map(c => (
                    <td key={c.key} className={`px-3 py-2 ${c.mono ? 'font-mono' : ''}`}>
                      {it[c.key] || '-'}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-center">
                    {it.rut_dv_valido
                      ? <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700 rounded font-semibold">✗ REVISAR</span>
                      : <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">RUT falso</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-4" data-testid="tab-razon-social">
      <div className={`rounded-lg border p-4 flex items-center gap-3 ${sevCfg.color}`}>
        {sevCfg.icon}
        <div className="flex-1">
          <div className="font-semibold">{sevCfg.label}</div>
          <div className="text-xs opacity-80">
            El cron `razon_social_scan` corre diariamente a las 03:15 CLT y re-intenta resolver
            desde DTE/RCV/Alegra/Maestro. Los RUTs falsos (DV inválido) nunca se resuelven — se pueden borrar.
          </div>
        </div>
        <div className="text-[11px] opacity-70">Chequeado: {(data.checked_at || '').slice(0, 19).replace('T', ' ')}</div>
      </div>

      {totalFantasmas > 0 && (
        <div className="border border-gray-300 rounded-lg p-4 bg-gray-50 flex items-center justify-between gap-3">
          <div className="flex-1">
            <div className="font-semibold text-gray-800 text-sm">Proveedores fantasma detectados: {totalFantasmas}</div>
            <div className="text-xs text-gray-600 mt-0.5">
              RUTs con DV inválido (test/basura) sin OC ni DTE activo. Seguro para borrar.
            </div>
          </div>
          <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            <AlertDialogTrigger asChild>
              <button
                className="px-4 py-2 text-sm font-semibold bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                data-testid="btn-borrar-fantasmas"
                disabled={deleting}
              >
                {deleting ? 'Borrando…' : `Borrar ${totalFantasmas} fantasmas`}
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="dialog-borrar-fantasmas">
              <AlertDialogHeader>
                <AlertDialogTitle>Borrar proveedores fantasma</AlertDialogTitle>
                <AlertDialogDescription>
                  Se van a borrar los proveedores con placeholder que tienen RUT inválido Y no aparecen en ninguna OC ni DTE.
                  Los que sí tienen actividad quedan protegidos automáticamente. Esta acción no se puede deshacer.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="btn-cancel-borrar-fantasmas">Cancelar</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleBorrarFantasmas}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="btn-confirm-borrar-fantasmas"
                >Sí, borrar</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}

      {lastDeleteResult && (
        <div className="border border-emerald-200 rounded-lg p-3 bg-emerald-50 text-xs">
          <div className="font-semibold text-emerald-800">✓ Última operación de borrado:</div>
          <div className="text-emerald-700 mt-1">
            Borrados: {lastDeleteResult.borrados} · Protegidos (con actividad): {lastDeleteResult.protegidos}
          </div>
        </div>
      )}

      {renderTable(
        'Proveedores maestros', proveedores,
        [{ key: 'rut', label: 'RUT', mono: true }, { key: 'nombre_actual', label: 'Nombre actual' }],
        'table-razon-social-proveedores',
      )}

      {renderTable(
        'Órdenes de compra', ordenes,
        [
          { key: 'oc', label: 'N° OC', mono: true },
          { key: 'rut', label: 'RUT', mono: true },
          { key: 'nombre_actual', label: 'Nombre actual' },
          { key: 'fecha_creacion', label: 'Fecha creación' },
        ],
        'table-razon-social-ocs',
      )}

      {renderTable(
        'Alegra contacts', alegra,
        [
          { key: 'alegra_id', label: 'Alegra ID', mono: true },
          { key: 'rut', label: 'RUT', mono: true },
          { key: 'nombre_actual', label: 'Nombre actual' },
        ],
        'table-razon-social-alegra',
      )}
    </div>
  );
}

